import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { groupDuplicateSites, type SiteCandidate } from '../duplicateSites.js';
import { useJobAccess } from '../useJobAccess.js';

/**
 * "These jobs look like the same site" — the office's prompt to merge a duplicate away.
 *
 * The merge screen has always existed, but only on a job's own detail page, so finding a duplicate
 * meant already knowing you had one. This surfaces it where the jobs actually are.
 *
 * Computed IN-MEMORY over the list the screen already holds, the same way the free-text search on
 * this page works and the same way `MyClock` does its check — no extra round-trip, and it stays
 * correct on a phone with bad signal.
 *
 * "Review & merge" links to the FIRST job in the cluster, which is where `JobMergeFlow` lives. That
 * job's picker now ranks its own likely duplicates to the top, so the two halves line up: this says
 * which sites to look at, that screen does the merging.
 *
 * Renders nothing when there is nothing to report — no empty banner, no "0 duplicates" state.
 */

export interface DuplicateSitesBannerProps {
  jobs: readonly SiteCandidate[];
}

export function DuplicateSitesBanner({ jobs }: DuplicateSitesBannerProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { canMerge } = useJobAccess();
  const clusters = groupDuplicateSites(jobs);

  // The banner exists to send people to the merge, so it is noise to anyone who may not merge.
  if (clusters.length === 0 || !canMerge) return null;

  return (
    <section
      // Not role="alert": this is standing information about existing data, not something that just
      // happened, and an assertive live region would interrupt a screen-reader user mid-task.
      aria-labelledby="duplicate-sites-heading"
      // Owns its own bottom margin so the caller renders no wrapper — a wrapper would leave a gap
      // on every jobs page that has no duplicates, which is most of them.
      className="mb-6 rounded-md border border-warning/30 bg-warning/10 p-4"
    >
      <h2
        id="duplicate-sites-heading"
        className="flex items-center gap-2 font-semibold text-foreground text-sm"
      >
        <TriangleAlert aria-hidden="true" className="size-4" />
        {t('jobs:duplicates.heading', { count: clusters.length })}
      </h2>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">{t('jobs:duplicates.body')}</p>
        <button
          type="button"
          className="inline-flex h-9 items-center rounded-md px-3 font-medium text-primary-ink text-sm underline underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-expanded={expanded}
          aria-controls="duplicate-sites-groups"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? t('jobs:duplicates.hideReview') : t('jobs:duplicates.reviewToggle')}
        </button>
      </div>

      <ul id="duplicate-sites-groups" className={expanded ? 'mt-3 space-y-3' : 'hidden'}>
        {clusters.map((cluster) => (
          <li
            key={cluster[0].id}
            className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
          >
            <ul className="min-w-0 space-y-0.5">
              {cluster.map((job) => (
                <li key={job.id} className="truncate text-foreground text-sm">
                  {job.jobName}
                </li>
              ))}
            </ul>
            <Link
              to="/jobs/$id"
              params={{ id: String(cluster[0].id) }}
              className="shrink-0 font-medium text-primary-ink text-sm underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('jobs:duplicates.review')}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
